import { Notification, NotificationButton } from '@appTypes/Notifications';
import { QueryFilter } from '@bett3r-dev/teselly-client-library';
import useAuth, { AuthContextType } from '@contexts/useAuth/useAuth';
import useBackend, { BackendContextType } from '@contexts/useBackend/useBackend';
import { IconBooks, IconCreditCard, IconMessage, IconPackage, IconSettings, IconShoppingBag, IconUsers } from '@tabler/icons-react';
import { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from '@components/common/Toast/Toast';
import { NotificationsReadmodelType } from '@bett3r-dev/teselly-domain';

export const notificationTypes: Record<string, { color: string; icon: JSX.Element }> = {
  inventory: { color: 'var(--mantine-color-red-8)', icon: <IconPackage size={16} /> },
  publication: { color: 'var(--mantine-color-blue-8)', icon: <IconShoppingBag size={16} /> },
  subscription: { color: 'var(--mantine-color-violet-8)', icon: <IconCreditCard size={16} /> },
  catalog: { color: 'var(--mantine-color-orange-8)', icon: <IconBooks size={16} /> },
  interactions: { color: 'var(--mantine-color-teal-8)', icon: <IconUsers size={16} /> },
  system: { color: 'var(--mantine-color-gray-6)', icon: <IconSettings size={16} /> },
  default: { color: 'var(--mantine-color-gray-6)', icon: <IconMessage size={16} /> }
};


export type NotificationsContextType = {
  notifications: Notification[];
  pageNotifications: Notification[];
  getPageNotifications: ( page: number, limit: number, filters?:any ) => void;
  setNotifications: React.Dispatch<React.SetStateAction<Notification[]>>;
  markAllAsRead: () => void;
  markAsRead: ( id: string ) => void;
  handleAction: ( command: NotificationButton['command']) => Promise<void>;
  hasUnread: boolean;
  notificationCount: number | undefined;
  getNotificationsCount: ( filter?:any ) => Promise<any>;
};

const notificationsContext = createContext<NotificationsContextType | undefined>( undefined );

export function ProvideNotifications({ children }: { children: ReactNode }) {
  const notifications = useProvideNotifications();
  return <notificationsContext.Provider value={notifications}>{children}</notificationsContext.Provider>;
}

export default function useNotifications() {
  return useContext( notificationsContext );
}

function useProvideNotifications(): NotificationsContextType {
  const { backend } = useBackend() as BackendContextType;
  const { user } = useAuth() as AuthContextType;
  const navigate = useNavigate();
  const [ notifications, setNotifications ] = useState<Notification[]>([]);
  const [ pageNotifications, setPageNotifications ] = useState<Notification[]>([]);
  const [ notificationCount, setNotificationCount ] = useState<number>();
  const [ hasUnread, setHasUnread ] = useState( false ); 
  const toastedNotificationsRef = useRef<Set<string>>( new Set());
  // Load mock / suscribe to backend
  const updateNotifications = ( incoming: Notification | Notification[]) => {
    const newOnes = Array.isArray( incoming ) ? incoming : [incoming];

    setNotifications( prev => {
      const byId = new Map( prev.map( n => [ n.id, n ]));
      newOnes.forEach( n => byId.set( n.id, n ));
      return Array
        .from( byId.values())
        .sort(( a, b ) =>
          new Date( b.timestamp ).getTime() - new Date( a.timestamp ).getTime());
    });
  };
  
  useEffect(() =>{
    if( user?.id && backend ){
        const closeNotifications = backend.subscriptions.Notifications(
          { filter: [{ field: 'userId', op: 'eq', value: user.id }], limit: 20, sort: [{ field: 'timestamp', direction: 'desc' }]},
          ( notificationsChange: any ) => {
            if ( notificationsChange?.type === 'snapshot' ) {
              updateNotifications( notificationsChange.data.items );
              return;
            }
            if ( notificationsChange?.data ) {
              updateNotifications( notificationsChange.data );
              if ( toastedNotificationsRef.current.has( notificationsChange.data.id )) return;
              if ( notificationsChange.data?.timestamp &&
                  ( new Date().getTime() - new Date( notificationsChange.data.timestamp ).getTime()) <= 5000 ) {
                  toastedNotificationsRef.current.add( notificationsChange.data.id );
                  toast.info({
                    title: notificationsChange.data?.title || 'Notificacion',
                    message: notificationsChange.data?.content
                  });
                }
              }
          }
        );
        //TODO: no se suscribe
        // const closeCountNotifications = backend.subscriptions.NotificationsReadmodelCount(
        //   { filter: [{ field: 'userId', op: 'eq', value: user.id }]},
        //   ( notificationsCountChange: any ) => {
        //     if ( notificationsCountChange?.data ) {
        //       console.log( 'notificationsCountChange', notificationsCountChange );
        //       setNotificationCount( notificationsCountChange.data );
        //     }
        //   }
        // );
        return () => {
          if( closeNotifications ){
            closeNotifications.close();
          }
          // closeCountNotifications.close();
        };
    }
  },[ backend, user ]);
  useEffect(() => {
    if( backend ){
      setHasUnread( notifications.some( n => !n.read ));
      backend.queries.NotificationsCount({}).then(( count:any ) => setNotificationCount( count.count ));
    }
  }, [ notifications, backend ]);
  
  
  // actions
  const getNotificationsCount = async ( filters?: QueryFilter<NotificationsReadmodelType>[]) =>{
    if( filters?.length ){
      return backend.queries.NotificationsCount({ filter: [...filters]});
    }else{
      return backend.queries.NotificationsCount({});
    }
  };
  const getPageNotifications = ( page: number, limit: number, filters?: QueryFilter<NotificationsReadmodelType>[]) => {
    if( filters?.length ){
      backend.queries.Notifications({ filter: [...filters], limit, after: ( page - 1 ) * limit })
      .then(( notifications:any ) => setPageNotifications( notifications ));
    }else{
      backend.queries.Notifications({ limit, after: ( page - 1 ) * limit })
      .then(( notifications:any ) => setPageNotifications( notifications ));
    }
  };
  
  const markAllAsRead = () => {
    if ( notifications?.length ) {
      const unreadIds: string[] = [];     
      const updatedNotifications = [...notifications]
        .map( n => { 
          if( !n.read ){
            n.read = true;
            unreadIds.push( n.id );
          }
          return n;
        });
      const updatedPageNotifications = [...pageNotifications]
      .map( n => { 
          if( !n.read ){
            n.read = true;
            unreadIds.push( n.id );
          }
          return n;
        });
      setNotifications( updatedNotifications );
      setPageNotifications( updatedPageNotifications );
      unreadIds.forEach( id => backend.commands.MarkNotificationAsRead({ id }));
    }
    return [];
  };

  const markAsRead = ( id: string ) => {
    if ( !notifications ) return;
    
    const index = notifications.findIndex( n => n.id === id );
    const indexPage = pageNotifications.findIndex( n => n.id === id );
    
    const notificationNeedsUpdate = index !== -1 && !notifications[index].read;
    const pageNotificationNeedsUpdate = indexPage !== -1 && !pageNotifications[indexPage].read;
    
    if ( notificationNeedsUpdate || pageNotificationNeedsUpdate ) {
      backend.commands.MarkNotificationAsRead({ id });
      
      if ( notificationNeedsUpdate ) {
        const updatedNotifications = [...notifications];
        updatedNotifications[index] = { ...updatedNotifications[index], read: true };
        setNotifications( updatedNotifications );
      }
      
      if ( pageNotificationNeedsUpdate ) {
        const updatedPageNotifications = [...pageNotifications];
        updatedPageNotifications[indexPage] = { ...updatedPageNotifications[indexPage], read: true };
        setPageNotifications( updatedPageNotifications );
      }
    }
  };

  const handleAction = async ( command: NotificationButton['command']) => {
    if ( !command ) return;
    if ( command.name === 'navigate' && command.arguments[0]) {
      navigate( command.arguments[0]);
    } else {
      await ( backend.commands as Record<string, ( ...args: unknown[]) => Promise<unknown>> )[command.name]( ...command.arguments );
      //TODO: deberíamos cambiar las actions o algo despues de ejecutarlo exitosamente si es un command?
    }
  };


  return {
    notifications,
    pageNotifications,
    getPageNotifications,
    setNotifications,
    markAllAsRead,
    markAsRead,
    handleAction,
    hasUnread,
    notificationCount,
    getNotificationsCount
  };
}

